import React from 'react';

/**
 * Whether the page currently in the outlet is a real page or the Not-Found one.
 *
 * The breadcrumb trail is derived from the URL's segments, not from a matched
 * route, so a made-up path rendered a confident, well-formed trail above a 404
 * body — "Home › Admin › All Roles" over "Page Not Found" — lending the address
 * a legitimacy it does not have (QA ledger NAV-002).
 *
 * It cannot be derived in the layout: the registered-route list the breadcrumbs
 * already consult holds the PLUGIN routes only, so testing the path against it
 * would call the shell's own pages (/settings, /drafts, /companies/:id/members)
 * not-found too. The page that actually rendered is the only thing that knows,
 * so it says so.
 */
const RouteOutcomeContext = React.createContext<{
  notFound: boolean;
  setNotFound: (value: boolean) => void;
}>({ notFound: false, setNotFound: () => {} });

export const RouteOutcomeProvider: React.FC<{
  notFound: boolean;
  setNotFound: (value: boolean) => void;
  children: React.ReactNode;
}> = ({ notFound, setNotFound, children }) => {
  const value = React.useMemo(() => ({ notFound, setNotFound }), [notFound, setNotFound]);
  return <RouteOutcomeContext.Provider value={value}>{children}</RouteOutcomeContext.Provider>;
};

/** Called by the Not-Found page. Clears itself when a real page replaces it. */
export const useReportNotFound = (): void => {
  const { setNotFound } = React.useContext(RouteOutcomeContext);
  React.useEffect(() => {
    setNotFound(true);
    return () => setNotFound(false);
  }, [setNotFound]);
};
